import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Mountain } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { openCheckout } from "@/lib/payments";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

export const SponsorClimbDialog = ({ peak, onClose }) => {
  const { data: config } = useQuery({ queryKey: ["payments-config"], queryFn: async () => (await api.get("/payments/config")).data });
  const [form, setForm] = useState({ name: "", email: "", phone: "", amount: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setDone(null);
    setForm({ name: "", email: "", phone: "", amount: "", message: "" });
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount < 1) { toast.error("Please enter a sponsorship amount."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/sponsorships", { ...form, amount, peak: peak.peak });
      if (!data.payment) {
        setDone({ paid: false, message: data.message });
        return;
      }
      const resp = await openCheckout({
        keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
        description: `Sponsorship: ${peak.peak} climb`,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        tracking: { content_name: `Sponsorship: ${peak.peak}`, content_type: "donation", product_type: "sponsorship" },
      });
      const v = await api.post("/sponsorships/verify", {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        fbp: resp.fbp, fbc: resp.fbc,
      });
      setDone({ paid: true, message: v.data.message });
    } catch (err) {
      toast.error(err?.response ? formatApiError(err.response.data?.detail) : err.message || "Could not send the enquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!peak} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent data-testid="sponsor-climb-dialog" className="max-w-lg bg-ptf-bg border-ptf-primary/15 p-8">
        {peak && (done ? (
          <div data-testid="sponsor-success" className="py-4">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-ptf-accent text-ptf-primary mb-5"><Check className="h-6 w-6" /></span>
            <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">{done.paid ? "Thank you." : "Noted, with gratitude."}</h3>
            <p className="mt-3 text-sm text-ptf-text/75 leading-relaxed">{done.message}</p>
            <button type="button" data-testid="sponsor-close-btn" onClick={close} className="mt-6 text-xs uppercase tracking-[0.18em] underline underline-offset-4 text-ptf-primary hover:text-ptf-secondary">
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/60 flex items-center gap-2">
                <Mountain className="h-3.5 w-3.5" /> Sponsor a climb
              </p>
              <DialogTitle className="font-display uppercase text-3xl tracking-tight text-ptf-primary leading-none">{peak.peak}</DialogTitle>
              <DialogDescription className="text-xs text-ptf-text/60">
                {peak.continent} · {peak.elevation.toLocaleString("en-IN")} m. Your sponsorship backs this specific climb; the summit will be documented here when it happens.
              </DialogDescription>
            </DialogHeader>
            {config && !config.enabled && (
              <p data-testid="sponsor-payments-pending" className="border border-ptf-primary/15 bg-ptf-primary/[0.04] px-3 py-2.5 text-xs text-ptf-text/70 leading-relaxed">
                Online payments are opening soon. Your enquiry and amount are recorded, and we will write to you the moment they are live.
              </p>
            )}
            <form onSubmit={submit} className="space-y-4 mt-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="sponsor-name" required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
                <input data-testid="sponsor-email" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="sponsor-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
                <input data-testid="sponsor-amount" required type="number" min="1" value={form.amount} onChange={set("amount")} placeholder="Amount (INR) *" className={inputCls} />
              </div>
              <textarea data-testid="sponsor-message" value={form.message} onChange={set("message")} placeholder="A note, a question, or how you would like to be involved" rows={3} className={`${inputCls} resize-none`} />
              <button
                type="submit"
                data-testid="sponsor-submit-btn"
                disabled={loading}
                className="w-full bg-ptf-primary text-ptf-bg py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {config?.enabled ? "Sponsor this Climb" : "Send Sponsorship Enquiry"}
              </button>
            </form>
          </>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default SponsorClimbDialog;
